// WHICH CUSTOMER THIS PAGE IS SHOWING — said on every entry, not just the one
// that grew the switcher.
//
// The dashboard has said this since the switcher shipped (SocRoute's
// .soc-scope-banner). /choke, /devices and /fleet are separate entries with
// separate React trees and said nothing, so a provider pointed at customer B
// reached the containment ladder, the device jails and the fleet kill switch
// with no caption at all — and, before the funnel and the hydration this
// component is mounted alongside, those pages were still aimed at the account's
// default customer while the dashboard's banner promised otherwise.
//
// It renders for a cross-tenant principal and for nobody else. A tenant-bound
// operator has one customer, has never selected one, and never reads a whoami
// that says otherwise, so this returns null and their page is unchanged.
import { AlertTriangle, Server } from "lucide-react";
import { useTenantScope } from "../lib/tenantScope";
import { startTenantScopeHydration } from "./tenantHydration";
import "./tenantScopeBanner.css";

/**
 * "shell" — the entry has no scope caption of its own. The default, so an entry
 * added later says the whole sentence rather than silently saying nothing.
 * "dashboard" — the SOC route renders its own banner beside the switcher, so
 * only the state that banner cannot describe is shown here: a remembered
 * customer this console could not confirm, which is holding writes back.
 * "captioned" — the route already names the customer in its own header (the
 * scope pill on /choke and /devices reads "adanian-internal · tc plane
 * attached · 5 devices"), so the shell's paragraph restated it a second time —
 * and, because this banner is a SIBLING of the route rather than a child, it
 * never took the rail offset: it rendered at x=20, underneath the 232px fixed
 * rail, so the sentence an operator actually saw began mid-word. Removed on
 * those two routes rather than re-laid-out, because the fact was already on
 * screen in the header.
 *
 * WHAT "captioned" DOES NOT TURN OFF: the hydration barrier, the unconfirmed
 * REFUSAL and the "Confirming…" wait — all three are decided above this mode is
 * read. `tenantScope: "none"` would have dropped the barrier too, which is a
 * different and much worse change: the page would mount before the selected
 * customer resolved and paint one tenant's rows under another's name.
 */
export type TenantScopeBannerMode = "shell" | "dashboard" | "captioned";

export function TenantScopeBanner({ mode }: { mode: TenantScopeBannerMode }) {
  const scope = useTenantScope();

  // THE HOLD, stated wherever the operator is. Containment is being refused by
  // the funnel (tenantScopeRefusal), and a refusal an operator cannot see reads
  // as a broken console. Shown on the dashboard too, because its own banner
  // names a customer and cannot say that writes are not reaching one.
  if (scope.unconfirmed) {
    return (
      <div className="console-scope-banner console-scope-banner--unconfirmed" role="alert">
        <AlertTriangle size={16} />
        <span>
          <b>Customer unconfirmed.</b>{" "}
          {scope.claimed ? (
            <>
              This console last showed <b>{scope.claimed}</b>, and could not confirm your account still reaches
              it.
            </>
          ) : (
            <>This console could not confirm which customer it is pointed at.</>
          )}{" "}
          Reads below are the customer your account resolves to by default. Containment is being refused until a
          customer is chosen.
        </span>
        <button type="button" onClick={() => void startTenantScopeHydration({ retry: true })}>
          Retry
        </button>
      </div>
    );
  }

  /*
   * STILL DECIDING — AND THIS RUNS IN BOTH MODES, WHICH IS THE POINT.
   *
   * At this moment this banner is the ENTIRE page: the shell does not mount the
   * route until the barrier settles (app/render.tsx), so the wait is not spent
   * out of some read's own deadline and reported as a gateway that never
   * answered.
   *
   * This branch used to sit BELOW the dashboard early return, so a returning
   * MSSP operator — the one case that has a remembered customer to confirm —
   * landed on `/` and got a completely blank page: no header, no nav, no
   * switcher, no skeleton, no text, just the body background, for as long as
   * whoami and /api/tenants took, up to the 8s cap. Only when the deadline
   * expired did any sentence appear, and it was the amber "could not confirm"
   * one. /choke and /devices named the wait correctly throughout, because they
   * take the shell default and reached this branch — so the console already
   * knew how to say it, on every entry except the one a returning operator
   * lands on.
   *
   * The dashboard's own scope banner cannot cover this: the route is unmounted
   * during the hold, so it does not exist yet. `hydrating` and
   * `mode === "dashboard"` are not the same question, and the early return
   * below must not answer both.
   *
   * The barrier itself is untouched. It is the real invariant — it stops one
   * tenant's rows being painted under another tenant's name — and the 8s is a
   * cap on a hung control plane, not a fixed cost. The defect was only that
   * "loading" had nowhere to be said, which is the four-state rule collapsing
   * into three.
   */
  if (scope.hydrating) {
    return (
      // `--pending` is the SKIN of the same distinction the words already made.
      // role="status" and "Confirming…" keep this from reading as the refusal;
      // the box did not, so a wait and a settled provider view were drawn
      // identically. Scope (board 14) lists "Resolving scope…" as its own
      // state and States (board 05) tones an unresolved reading neutral —
      // never the tone of an answer. Nothing about what is said changes here.
      <div className="console-scope-banner console-scope-banner--pending" role="status">
        <Server size={16} />
        <span>
          Confirming that your account still reaches{" "}
          {scope.claimed ? <b>{scope.claimed}</b> : <>the customer this console last showed</>}…
        </span>
      </div>
    );
  }

  // Only the shell mode says the rest of it; see the type above.
  if (mode !== "shell") return null;

  // A customer the operator picked. This is the sentence the blocker was about:
  // it is true because lib/api.ts names this customer on every read AND every
  // write that leaves this page, not because the page happens to be showing it.
  if (scope.selected) {
    return (
      <div className="console-scope-banner" role="status">
        <Server size={16} />
        <span>
          <b>Provider view.</b> Showing <b>{scope.selected}</b> only — not your whole book of business — and any
          containment fired from this page lands there.
        </span>
      </div>
    );
  }

  // A provider who has not picked anyone. Nothing is wrong, but "the customer
  // your account defaults to" is still ONE customer, and the numbers on these
  // pages are not the estate's. `crossTenant` is undefined until some whoami
  // has answered, and only an explicit true says a customer had to be resolved.
  /*
   * "NO CUSTOMER CHOSEN" IS FALSE WHEN ONE WAS CHOSEN AND DROPPED.
   *
   * A remembered customer the roster no longer offers is forgotten outright —
   * correct, because leaving it would let a later roster read silently adopt
   * the name again. But the provider branch below then said "No customer
   * chosen, so this page shows the one your account resolves to", and the
   * operator DID choose one. They are shown a different customer's estate and
   * told it is what they asked for.
   *
   * This is not a refusal: the funnel deliberately releases writes here (a
   * roster that answered is evidence, and holding on it would strand a provider
   * whose customer was off-boarded). It is a correction to the sentence.
   */
  if (scope.forgotten && !scope.selected) {
    return (
      <div className="console-scope-banner" role="status">
        <Server size={16} />
        <span>
          <b>Customer no longer available.</b> This console was pointed at <b>{scope.forgotten}</b>, which your
          account no longer reaches, so it now shows
          {scope.serverTenant ? (
            <>
              {" "}
              <b>{scope.serverTenant}</b>
            </>
          ) : (
            <> the customer your account resolves to</>
          )}
          , and any containment fired here lands there. Pick a customer on the dashboard to point it elsewhere.
        </span>
      </div>
    );
  }

  if (scope.crossTenant === true) {
    return (
      <div className="console-scope-banner" role="status">
        <Server size={16} />
        <span>
          <b>Provider view.</b> No customer chosen, so this page shows the one your account resolves to
          {scope.serverTenant ? (
            <>
              {" — "}
              <b>{scope.serverTenant}</b>
            </>
          ) : null}
          , and any containment fired here lands there. Pick a customer on the dashboard to point it elsewhere.
        </span>
      </div>
    );
  }

  return null;
}
